import React, { useEffect, useState, useCallback } from "react";
import { Ear, Plus, Loader2, Clock, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader, { ActionRow } from "@/components/PageHeader";
import ConfirmDialog from "@/components/ConfirmDialog";

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { timeZone: "America/Chicago", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

const STATUS = {
  pending: { label: "Pending review", cls: "bg-amber-100 text-amber-700", Icon: Clock },
  approved: { label: "Approved & sent", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  rejected: { label: "Returned", cls: "bg-red-100 text-red-700", Icon: XCircle },
};

function DraftDialog({ open, setOpen, onDone }) {
  const [form, setForm] = useState({ title: "", message: "" });
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) setForm({ title: "", message: "" }); }, [open]);

  const submit = async () => {
    if (!form.title.trim() || !form.message.trim()) { toast.error("Title and message are required"); return; }
    setLoading(true);
    try {
      await api.post("/member/announcement-drafts", form);
      toast.success("Sent to your coach for approval");
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="draft-announcement-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Draft a Rally Call message</DialogTitle>
          <DialogDescription>Your coach reviews this before it goes out to the team. It won't be sent until they approve it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Fundraiser car wash" data-testid="draft-title-input" /></div>
          <div className="space-y-2"><Label>Message</Label><Textarea rows={5} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} placeholder="What should the team know?" data-testid="draft-message-input" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="draft-submit-btn">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit for approval
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MemberAnnouncements() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [del, setDel] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/member/announcement-drafts");
      setItems(data.items || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const withdraw = async () => {
    try {
      await api.delete(`/member/announcement-drafts/${del.id}`);
      toast.success("Draft withdrawn");
      setDel(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDel(null); }
  };

  return (
    <div>
      <PageHeader title="Rally Call" subtitle="Draft Rally Call messages for your coach to review and send to the team" />
      <ActionRow testId="new-draft-row">
        <Button onClick={() => setOpen(true)} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="new-draft-btn"><Plus className="mr-2 h-4 w-4" /> Draft message</Button>
      </ActionRow>

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-16 text-center" data-testid="announcements-empty">
          <Ear className="mx-auto mb-3 h-8 w-8 text-slate-300" />
          <p className="text-sm font-medium text-slate-500">No Rally Call messages yet</p>
          <p className="mt-1 text-xs text-slate-400">Draft one and your coach will review it before it's sent.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="announcements-list">
          {items.map((a) => {
            const s = STATUS[a.status] || STATUS.pending;
            return (
              <div key={a.id} className="rounded-xl border border-slate-200 p-4" data-testid={`draft-item-${a.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="font-semibold text-slate-900">{a.final_title || a.title}</p>
                  <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${s.cls}`} data-testid={`draft-status-${a.id}`}>
                    <s.Icon className="h-3 w-3" /> {s.label}
                  </span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{a.final_message || a.message}</p>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-400">
                  <span>Submitted {fmt(a.created_at)}</span>
                  {a.status === "approved" && <span className="text-emerald-600">Sent to {a.recipient_count} member{a.recipient_count === 1 ? "" : "s"}{a.audience ? ` · ${a.audience}` : ""}</span>}
                  {a.status === "rejected" && a.reject_reason && <span className="text-red-600">Reason: {a.reject_reason}</span>}
                  {a.status === "pending" && (
                    <button onClick={() => setDel(a)} className="inline-flex items-center gap-1 font-medium text-slate-500 hover:text-red-600" data-testid={`withdraw-${a.id}`}>
                      <Trash2 className="h-3 w-3" /> Withdraw
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <DraftDialog open={open} setOpen={setOpen} onDone={load} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Withdraw draft?"
        description={del ? `"${del.title}" will be removed before your coach reviews it.` : ""}
        confirmText="Withdraw" destructive onConfirm={withdraw} testId="withdraw-dialog" />
    </div>
  );
}
